import type { Money } from "@shared/types/roomOffer";
import type { MoneyTotals } from "@shared/types/offerFunnel";
import type { Translate } from "../../i18n/translate";
import type { Formatters } from "../../i18n/format";

/**
 * Money is never summed across currencies, pipeline is never merged with revenue,
 * per-mille 0 (earned nothing) and null (unknowable) stay apart, engaged is a proxy.
 */

/** `Money.amount` is major units: nothing divides by a hundred. Decimals only when present. */
export function formatMoney(fmt: Formatters, money: Money): string {
  const amount = fmt.number(money.amount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${amount} ${money.currency}`;
}

/**
 * A whole money bag: every currency, richest first, joined — never added.
 *
 * Empty is not an error and not a gap: it means nothing priced landed in this
 * window. The caller decides how to say that, because "0" needs a currency it
 * does not have — see {@link moneyIsEmpty}.
 */
export function formatMoneyTotals(
  fmt: Formatters,
  totals: MoneyTotals
): string {
  return totals.map((m) => formatMoney(fmt, m)).join(" + ");
}

/** Nothing priced. Distinct from "we could not measure it". */
export function moneyIsEmpty(totals: MoneyTotals): boolean {
  return totals.length === 0 || totals.every((m) => m.amount === 0);
}

/**
 * The largest single-currency subtotal, for a chart's y-scale and for ranking.
 *
 * A chart plots one number per column, so it plots the biggest currency's
 * subtotal and says which currency in its axis — the alternative is adding
 * kronor to euros to decide a bar height, which is the same mistake as adding
 * them to print a total, just harder to spot. `@shared/offerFunnel` ranks rows
 * on exactly this value for the same reason.
 */
export function primaryMoney(totals: MoneyTotals): Money | null {
  return totals[0] ?? null;
}

/**
 * Revenue per 1000 impressions.
 *
 * - `null` → `—`. Unknowable: no impressions, or mixed currency.
 * - `0` → `0`. Known, and the answer is nothing. **Not** a dash.
 * - anything else → `6 750 kr`.
 *
 * The zero carries no currency because zero has none; the type says so
 * (`revenuePerMilleCurrency` is null whenever there is no single one, the
 * honest zero included).
 */
export function formatPerMille(
  fmt: Formatters,
  value: number | null,
  currency: string | null
): string {
  if (value === null) return "—";
  const amount = fmt.number(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: value < 10 ? 2 : 0,
  });
  return currency ? `${amount} ${currency}` : amount;
}

/**
 * The tooltip that belongs beside whatever {@link formatPerMille} returned.
 * A dash is "not measured", never "zero"; a zero is a number, not an absence.
 */
export function perMilleHint(t: Translate, value: number | null): string {
  if (value === null) return t("offers:offerRevenueFormat.perMilleUnknown");
  if (value === 0) return t("offers:offerRevenueFormat.perMilleZero");
  return t("offers:offerRevenueFormat.perMilleHint");
}

/** The report's four promises, stated once so no view can soften them. */
export type RevenueNote = "engagedProxy" | "revenueRule" | "pipeline" | "grain";

export function revenueNote(t: Translate, note: RevenueNote): string {
  return t(`offers:offerRevenueFormat.${note}Note`);
}
